import { QueryClient } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink, httpBatchStreamLink, splitLink } from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import superjson from "superjson";
import type { AppRouter } from "@kipo/api/router";
import { authedFetch } from "./session";

export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => {
        const code = (err as { data?: { code?: string } }).data?.code;
        if (code === "UNAUTHORIZED" || code === "FORBIDDEN" || code === "NOT_FOUND" || code === "PAYMENT_REQUIRED") return false;
        return count < 2;
      },
    },
  },
});

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    // la chat di Kipo AI arriva a eventi (testo, passaggi dell'agente, grafici)
    splitLink({
      condition: (op) => op.path === "ai.chatStream",
      true: httpBatchStreamLink({ url: "/trpc", transformer: superjson, fetch: (url, init) => authedFetch(url, init as RequestInit) }),
      false: httpBatchLink({ url: "/trpc", transformer: superjson, fetch: (url, init) => authedFetch(url, init as RequestInit) }),
    }),
  ],
});

/** Messaggio leggibile da un errore tRPC. */
export function errorMessage(err: unknown): string {
  if (!err) return "";
  const e = err as { message?: string; data?: { zod?: { fieldErrors?: Record<string, string[]> } } };
  const fields = e.data?.zod?.fieldErrors;
  if (fields) {
    const first = Object.entries(fields)[0];
    if (first) return `${first[0]}: ${first[1]?.[0] ?? "valore non valido"}`;
  }
  return e.message ?? "Qualcosa non ha funzionato";
}
